import { Box, Chip, Divider, Modal, Typography } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ScheduleIcon from "@mui/icons-material/Schedule";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { FontFamily } from "../theme/fontFamily";
import { fontWeight } from "../theme/fontWeight";
import { fontSize } from "../theme/fontSize";
import { ThemeColor } from "../theme/color";
import {
  PayoutModalHeader,
  payoutModalBodySx,
  payoutModalStyle,
} from "./payoutModal";

const PHASE_A = "#15539E";
const PHASE_B = "#0F7B45";

/**
 * The seven payout steps, in the order they must be run.
 *
 * This is the single source of truth for the help modal AND for the one-line
 * hints on the Export menu, so the two can never say different things.
 */
export const WORKFLOW_STEPS = [
  {
    step: 1,
    phase: "A",
    action: "export",
    title: "Export For University",
    summary: "Generate the applicant list for the university to validate.",
    output: "Excel list of students, sent to the university for verification.",
    precondition:
      "Students must already exist in the database with visa status approved.",
  },
  {
    step: 2,
    phase: "A",
    action: "import",
    title: "Import University Transaction",
    summary:
      "Upload the sheet back after the university has marked approvals, rejections and amounts.",
    updates: "Status, amount and notes are saved. Progress moves to 1/4.",
    columns: [
      {
        header: "Commission Status",
        values: "approved / rejected",
        effect: "Changes the transaction status in the database.",
      },
      {
        header: "University Amount",
        values: "Numeric (e.g. 500, 1250)",
        effect: "Records the funds expected from the university.",
      },
      {
        header: "Commission Status Notes",
        values: "Free text (e.g. Student dropped out)",
        effect: "Logs the reason for a rejection.",
      },
    ],
  },
  {
    step: 3,
    phase: "A",
    action: "export",
    title: "Export For University Invoice",
    summary: "Create the billing invoice, for approved students only.",
    output:
      "Invoice sheet with approved students and totals. Send it to the university for payment.",
    precondition:
      "Step 2 must be done, and approved students must have transactions created.",
  },
  {
    step: 4,
    phase: "A",
    action: "import",
    title: "Import Credited Amount",
    summary: "Mark university invoices as paid once the funds arrive.",
    updates: "The Credited stage is marked complete in the Tracking Station.",
    columns: [
      {
        header: "Credited",
        values: "Y / Yes",
        effect: "Marks the university invoice as paid.",
      },
    ],
  },
  {
    step: 5,
    phase: "B",
    action: "export",
    title: "Export For Organisation",
    summary: "Send the student status report to the organisation (agent).",
    output: "Status report of approved / rejected students, for agent reference.",
    precondition:
      "The student must belong to an organisation, and the university commission process must be complete.",
  },
  {
    step: 6,
    phase: "B",
    action: "export",
    title: "Export For Organisation Invoice",
    summary: "Generate the payout invoice with the agent's calculated commission.",
    output: "Payout invoice sheet with the calculated amounts for the organisation.",
    precondition:
      "The university commission must be received and marked Credited in step 4.",
  },
  {
    step: 7,
    phase: "B",
    action: "import",
    title: "Import Settled Amount",
    summary: "Mark payouts as settled after the funds are transferred to the agent.",
    updates:
      "The Settled stage is marked complete and the transaction cycle is closed.",
    columns: [
      {
        header: "Settled",
        values: "Y / Yes",
        effect: "Marks the agent payout transaction as completed.",
      },
    ],
  },
];

/** Menu-item hint: "Step 3 of 7 — Create the billing invoice...". */
export const stepHint = (title) => {
  const found = WORKFLOW_STEPS.find((s) => s.title === title);
  if (!found) return "";
  return `Step ${found.step} of ${WORKFLOW_STEPS.length} — ${found.summary}`;
};

const RULES = [
  {
    icon: <ScheduleIcon fontSize="small" />,
    title: "Run them in order",
    body: "No step can be skipped. Each one depends on the step before it.",
  },
  {
    icon: <VerifiedUserIcon fontSize="small" />,
    title: "University approval comes first",
    body: "Nothing in Phase B (steps 5-7) can happen until the university has approved in step 2.",
  },
];

const PhaseLabel = ({ phase }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      mb: 2,
      mt: 1,
    }}
  >
    <Box
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: "6px",
        bgcolor: phase === "A" ? PHASE_A : PHASE_B,
        color: "#FFFFFF",
        fontFamily: FontFamily.Poppins,
        fontWeight: fontWeight.W600,
        fontSize: "0.72rem",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
      }}
    >
      {phase === "A" ? "Phase A · Collecting from universities" : "Phase B · Paying organisations"}
    </Box>
    <Divider sx={{ flex: 1 }} />
  </Box>
);

const StepCard = ({ item }) => {
  const colour = item.phase === "A" ? PHASE_A : PHASE_B;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        p: 2,
        mb: 1.5,
        border: "1px solid #E2E8F0",
        borderLeft: `4px solid ${colour}`,
        borderRadius: "10px",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 30,
          height: 30,
          borderRadius: "50%",
          bgcolor: colour,
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FontFamily.Poppins,
          fontWeight: fontWeight.W600,
          fontSize: "0.85rem",
        }}
      >
        {item.step}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {item.action === "export" ? (
            <CloudDownloadIcon fontSize="small" sx={{ color: colour }} />
          ) : (
            <CloudUploadIcon fontSize="small" sx={{ color: "green" }} />
          )}
          <Typography
            fontFamily={FontFamily.Poppins}
            fontWeight={fontWeight.W600}
            fontSize={fontSize.h4.large}
            color="#0F172A"
          >
            {item.title}
          </Typography>
          <Chip
            size="small"
            label={item.action === "export" ? "Download" : "Upload"}
            sx={{
              height: 19,
              fontSize: "0.65rem",
              bgcolor: item.action === "export" ? "#EFF6FF" : "#ECFDF5",
              color: item.action === "export" ? PHASE_A : PHASE_B,
            }}
          />
        </Box>

        <Typography
          sx={{ mt: 0.75, fontSize: "0.85rem", color: "#334155" }}
          fontFamily={FontFamily.Lato}
        >
          {item.summary}
        </Typography>

        {item.precondition && (
          <Typography sx={{ mt: 1, fontSize: "0.78rem", color: "#B45309" }}>
            <strong>Before you can run it:</strong> {item.precondition}
          </Typography>
        )}

        {item.output && (
          <Typography sx={{ mt: 0.5, fontSize: "0.78rem", color: "#64748B" }}>
            <strong>You get:</strong> {item.output}
          </Typography>
        )}

        {item.updates && (
          <Typography sx={{ mt: 0.5, fontSize: "0.78rem", color: "#64748B" }}>
            <strong>What changes:</strong> {item.updates}
          </Typography>
        )}

        {item.columns && (
          <Box
            sx={{
              mt: 1.25,
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.1fr 1fr 1.4fr",
                gap: 1,
                px: 1.25,
                py: 0.75,
                bgcolor: "#F8FAFC",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              <span>Excel column</span>
              <span>Valid values</span>
              <span>What it does</span>
            </Box>
            {item.columns.map((col) => (
              <Box
                key={col.header}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.1fr 1fr 1.4fr",
                  gap: 1,
                  px: 1.25,
                  py: 0.85,
                  borderTop: "1px solid #F1F5F9",
                  fontSize: "0.78rem",
                  color: "#334155",
                }}
              >
                <strong style={{ wordBreak: "break-word" }}>{col.header}</strong>
                <span style={{ color: "#0F172A" }}>{col.values}</span>
                <span style={{ color: "#64748B" }}>{col.effect}</span>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const PayoutWorkflowModal = ({ open, onClose }) => (
  <Modal open={Boolean(open)} onClose={onClose}>
    <Box sx={payoutModalStyle(880)}>
      <PayoutModalHeader
        title="How the payout workflow works"
        onClose={onClose}
      />

      <Box sx={{ ...payoutModalBodySx, backgroundColor: "#F8FAFC" }}>
        <Typography
          sx={{ fontSize: "0.85rem", color: "#475569", mb: 1 }}
          fontFamily={FontFamily.Lato}
        >
          Seven steps take a student from a university commission through to an
          agent payout. Run them top to bottom.
        </Typography>

        <PhaseLabel phase="A" />
        {WORKFLOW_STEPS.filter((s) => s.phase === "A").map((item) => (
          <StepCard key={item.step} item={item} />
        ))}

        <PhaseLabel phase="B" />
        {WORKFLOW_STEPS.filter((s) => s.phase === "B").map((item) => (
          <StepCard key={item.step} item={item} />
        ))}

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: "10px",
            border: "1px solid #FDE68A",
            backgroundColor: "#FFFBEB",
          }}
        >
          <Typography
            fontFamily={FontFamily.Poppins}
            fontWeight={fontWeight.W600}
            fontSize={fontSize.h4.large}
            color="#92400E"
            sx={{ mb: 1 }}
          >
            Rules that matter
          </Typography>
          {RULES.map((rule) => (
            <Box
              key={rule.title}
              sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1 }}
            >
              <Box sx={{ color: "#B45309", mt: "1px" }}>{rule.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#92400E" }}>
                  {rule.title}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#78350F" }}>
                  {rule.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Modal>
);

export default PayoutWorkflowModal;
